/**
 * REPSit.ai entry page — `/` after login (Beta-1 plan P6-01, Tim's Entry-page mockup + C0820/C0826).
 *
 *   logo · tagline "Your Team of Intelligent HOP Assistants"
 *   one field "How may we assist you today?"  [+ attach]  [mic]
 *   the ten Intelligent Assistants — only DREW active, the others ghosted
 *   "About" top-left · header actions and the user avatar live in the TopBar (P6-10)
 *
 * ⚑ D-UI-01 (UI review 2026-09-21, for Tim): the ten avatars were one size in two rows, so the eye
 * landed on nine ghosts and DREW had no visible action. Now the ACTIVE assistant(s) get a hero
 * (bigger avatar + "Access DREW →", D01's wording) and the coming nine sit in a smaller strip under
 * one caption. Everything Tim listed is still on the page; only the sizes and the button changed.
 *
 * Search (P6-03/P6-04/P5-11): results grouped under the field as you type; Enter on a single
 * strong match navigates directly ("Texas City explosion" → that case study, "log an OE
 * condition" → the Entry Portal). "+" hands a file to the Document Upload Portal; the mic uses the
 * Web Speech API when the browser has it. Wallpaper slot (P6-11): the CSS variable
 * `--repsit-entry-wallpaper` (an `url(...)`), set on :root by a season script later — no content.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, IconButton, Tooltip, Paper, List, ListItemButton, ListItemText,
  ListSubheader, Link, CircularProgress, Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ASSISTANTS } from '../../assistants/assistants';
import { ASSISTANT_PATHS, DA } from '../navigation/navigation.js';
import { useGlobalSearch } from '../search/useGlobalSearch.js';
import { setPendingFile } from '../search/pendingUpload.js';
import { useSpeechInput } from './useSpeechInput.js';
import { useAuth } from '../../contexts/AuthContext';
import { useUserOrganizations } from '../../assistants/drew/hooks/useOrganization';

export const ENTRY_TAGLINE = 'Your Team of Intelligent HOP Assistants';
export const ENTRY_PROMPT = 'How may we assist you today?';

/** The active assistant — hero treatment with D01's "Access DREW" call to action. */
function ActiveAssistantHero({ assistant, onOpen, onWelcome }) {
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 520, mx: 'auto' }} data-tour="entry-active-assistant">
      <Box
        component="img"
        src={assistant.avatar}
        alt={assistant.name}
        onClick={() => onOpen(assistant)}
        sx={{
          width: { xs: 150, sm: 190, md: 210 }, height: { xs: 150, sm: 190, md: 210 }, objectFit: 'contain', display: 'block', mx: 'auto',
          cursor: 'pointer', transition: 'transform 0.15s', '&:hover': { transform: 'translateY(-3px)' },
        }}
      />
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, lineHeight: 1.3 }}>
        {assistant.shortTitle}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: 'center', mt: 1.5 }}>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onOpen(assistant)}
          sx={{ textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 6 }}
          aria-label={`Access ${assistant.name} — ${assistant.shortTitle}`}
        >
          Access {assistant.name}
        </Button>
        <Button variant="text" onClick={onWelcome} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Welcome page · what&rsquo;s coming &amp; vote
        </Button>
      </Box>
    </Box>
  );
}

/** A coming assistant — ghosted, smaller, links to its card on the welcome page. */
function ComingAssistantTile({ assistant, onComing }) {
  return (
    <Tooltip title={`${assistant.name} — ${assistant.shortTitle}. Coming to REPSit.ai; beta testers vote on what comes next.`} arrow>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onComing(assistant)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onComing(assistant); }}
        aria-label={`${assistant.name} — Coming to REPSit.ai`}
        sx={{
          width: { xs: 84, sm: 104, md: 118 }, textAlign: 'center', cursor: 'pointer', outline: 'none',
          opacity: 0.5, filter: 'grayscale(0.6)', transition: 'opacity 0.15s',
          '&:hover': { opacity: 0.8 },
          '&:focus-visible': { boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}55`, borderRadius: 2 },
        }}
      >
        <Box
          component="img"
          src={assistant.avatar}
          alt={assistant.name}
          loading="lazy"
          sx={{ width: { xs: 76, sm: 96, md: 110 }, height: { xs: 76, sm: 96, md: 110 }, objectFit: 'contain', display: 'block', mx: 'auto' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.25, lineHeight: 1.3, fontSize: '0.68rem' }}>
          {assistant.shortTitle}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function EntryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const orgId = organizations[0]?.id || null;
  const { search, ensureLoaded, loading, open, logQuery } = useGlobalSearch({ organizationId: orgId });

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  const result = useMemo(() => search(query), [search, query]);
  const flat = useMemo(() => result.groups.flatMap((g) => g.items), [result]);
  useEffect(() => { if (query.trim().length >= 2) ensureLoaded(); }, [query, ensureLoaded]);
  useEffect(() => { setCursor(-1); }, [query]);

  const speech = useSpeechInput({ onResult: (text) => setQuery(text) });

  const submit = () => {
    const q = query.trim();
    if (!q) return;
    logQuery(q, result);
    const target = cursor >= 0 ? flat[cursor] : result.best || flat[0] || null;
    if (target) open(target, { query: q });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(-1, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); submit(); }
    else if (e.key === 'Escape') { setQuery(''); inputRef.current?.blur(); }
  };

  const onAttach = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    navigate(`${DA}/documents`);
  };

  const openAssistant = (a) => navigate(ASSISTANT_PATHS[a.id] || a.route || DA);
  const openComing = () => navigate('/dashboard#coming');
  const sorted = useMemo(() => [...ASSISTANTS].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), []);
  const activeAssistants = sorted.filter((a) => a.active);
  const comingAssistants = sorted.filter((a) => !a.active);
  const showResults = focused && query.trim().length >= 2;

  return (
    <Box
      data-tour="entry-page"
      sx={{
        minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, display: 'flex', flexDirection: 'column', alignItems: 'center',
        pt: { xs: 5, md: 6 }, px: 2, pb: 4, position: 'relative',
        backgroundImage: 'var(--repsit-entry-wallpaper, none)', backgroundSize: 'cover', backgroundPosition: 'center',
      }}
    >
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon fontSize="small" />}
        onClick={() => navigate('/guide')}
        sx={{ position: 'absolute', top: 6, left: 10, textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
      >
        About
      </Button>

      <Box component="img" src="/repsit-logo.png" alt="REPSit.ai" sx={{ height: { xs: 64, md: 88 }, width: 'auto', mb: 1.5 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '1.25rem', md: '1.5rem' }, textWrap: 'balance' }}>
        {ENTRY_TAGLINE}
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 720, position: 'relative' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          placeholder={ENTRY_PROMPT}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          inputProps={{ 'aria-label': ENTRY_PROMPT, autoComplete: 'off' }}
          InputProps={{
            sx: { borderRadius: 6, pl: 1, bgcolor: 'background.paper', boxShadow: 2 },
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="Attach a document to upload">
                  <IconButton aria-label="Attach a document" onClick={() => fileRef.current?.click()} size="small">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                {speech.supported && (
                  <Tooltip title={speech.listening ? 'Stop listening' : 'Speak your question'}>
                    <IconButton aria-label="Voice input" onClick={speech.toggle} size="small" color={speech.listening ? 'error' : 'default'}>
                      {speech.listening ? <MicOffIcon /> : <MicIcon />}
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton aria-label="Search" onClick={submit} size="small" color="primary">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.csv" onChange={onAttach} />
        {speech.error && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 2 }}>
            Voice input is unavailable ({speech.error}). Type your question instead.
          </Typography>
        )}

        {showResults && (
          <Paper elevation={6} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, mt: 1, zIndex: 10, maxHeight: 420, overflowY: 'auto', borderRadius: 2 }}>
            {result.groups.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? 'Looking through case studies, conditions and surveys…' : 'No matches yet — try a page name, a case study, a condition ID or "log an OE condition".'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {result.groups.map((g) => (
                  <React.Fragment key={g.kind}>
                    <ListSubheader disableSticky sx={{ lineHeight: '28px', fontWeight: 700 }}>{g.label}</ListSubheader>
                    {g.items.map((item) => {
                      const idx = flat.indexOf(item);
                      return (
                        <ListItemButton
                          key={item.id}
                          selected={idx === cursor}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { logQuery(query, result); open(item, { query: query.trim() }); }}
                        >
                          <ListItemText
                            primary={item.title}
                            secondary={item.snippet ? `…${item.snippet.before}${item.snippet.match}${item.snippet.after}` : item.subtitle}
                            primaryTypographyProps={{ fontWeight: item === result.best ? 700 : 500, noWrap: true }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          {item === result.best && <ArrowForwardIcon fontSize="small" color="primary" />}
                        </ListItemButton>
                      );
                    })}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}
      </Box>

      {/* Active assistant(s) — hero */}
      <Box sx={{ mt: { xs: 4, md: 5 }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
        {activeAssistants.map((a) => (
          <ActiveAssistantHero key={a.id} assistant={a} onOpen={openAssistant} onWelcome={openComing} />
        ))}
      </Box>

      {/* Coming assistants — one caption, one strip (Tim: ghosted, "Coming to REPSit.ai") */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: { xs: 4, md: 5 }, mb: 1, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>
        Coming to REPSit.ai
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 1, sm: 1.5, md: 2 }, maxWidth: 1100 }} data-tour="entry-coming-assistants">
        {comingAssistants.map((a) => <ComingAssistantTile key={a.id} assistant={a} onComing={openComing} />)}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        Beta testers vote on what comes next —{' '}
        <Link component="button" underline="hover" onClick={openComing} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
          see the welcome page
        </Link>
      </Typography>
    </Box>
  );
}
